import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

const hoy = format(new Date(), 'yyyy-MM-dd');
const num = v => parseFloat(v) || 0;
const COP = n => new Intl.NumberFormat('es-CO').format(Number(n) || 0);
const TIPOS_BANDEJA = [2.5, 2.8, 3.5];

function Pill({ activo, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700,
      border: `2px solid ${activo ? '#1a1a2e' : '#E5E7EB'}`,
      background: activo ? '#1a1a2e' : 'white', color: activo ? 'white' : '#374151',
    }}>{children}</button>
  );
}

const inputSt = { width: '100%', padding: '9px 10px', border: '2px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const labelSt = { fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', display: 'block', marginBottom: 4 };

// ─────────────────────────────────────────
// TAB ÓRDENES DE PRODUCCIÓN
// ─────────────────────────────────────────
function TabOrdenes({ esGerencia }) {
  const [ordenes, setOrdenes] = useState([]);
  const [mixes, setMixes] = useState([]);
  const [sabores, setSabores] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [rango, setRango] = useState('mes'); // hoy | mes
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ fecha: hoy, mix_id: '', kg_producidos: '', notas: '' });
  const [bandejas, setBandejas] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const desde = rango === 'hoy' ? hoy : format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const hasta = hoy;

  useEffect(() => {
    API.get('/produccion/mixes').then(r => setMixes(r.data)).catch(() => {});
    API.get('/produccion/sabores').then(r => setSabores(r.data)).catch(() => {});
  }, []);
  useEffect(() => { cargar(); }, [rango]);

  const cargar = () => {
    setCargando(true);
    API.get('/produccion/ordenes', { params: { desde, hasta } }).then(r => setOrdenes(r.data)).catch(() => toast.error('Error cargando órdenes')).finally(() => setCargando(false));
  };

  const abrirModal = () => { setForm({ fecha: hoy, mix_id: '', kg_producidos: '', notas: '' }); setBandejas([]); setModal(true); };
  const addBandeja = () => setBandejas(b => [...b, { sabor_id: '', tipo_bandeja: 2.5, cantidad: '' }]);
  const updateBandeja = (i, f, v) => setBandejas(b => b.map((row, idx) => idx === i ? { ...row, [f]: v } : row));
  const removeBandeja = (i) => setBandejas(b => b.filter((_, idx) => idx !== i));

  const kgEnBandejas = bandejas.reduce((t, b) => t + (num(b.tipo_bandeja) * num(b.cantidad)), 0);
  const merma = num(form.kg_producidos) - kgEnBandejas;

  const handleGuardar = async (enviar) => {
    if (!form.mix_id) { toast.error('Selecciona un mix'); return; }
    if (!form.kg_producidos || num(form.kg_producidos) <= 0) { toast.error('Ingresa los kg producidos'); return; }
    if (enviar && bandejas.length === 0) { toast.error('Agrega al menos una bandeja antes de enviar'); return; }
    if (enviar && bandejas.some(b => !b.sabor_id || !b.cantidad)) { toast.error('Completa todos los campos de bandejas'); return; }
    setGuardando(true);
    try {
      await API.post('/produccion/ordenes', { ...form, bandejas, enviar });
      toast.success(enviar ? '✅ Orden enviada — MPs descontadas' : '💾 Borrador guardado');
      setModal(false);
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'Error guardando'); }
    finally { setGuardando(false); }
  };

  const porFecha = {};
  ordenes.forEach(o => { const k = o.fecha.slice(0,10); if (!porFecha[k]) porFecha[k] = []; porFecha[k].push(o); });

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Pill activo={rango === 'hoy'} onClick={() => setRango('hoy')}>Hoy</Pill>
        <Pill activo={rango === 'mes'} onClick={() => setRango('mes')}>Este mes</Pill>
      </div>

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando...</div>
      ) : Object.keys(porFecha).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <div style={{ fontWeight: 600 }}>Sin órdenes en este período</div>
        </div>
      ) : Object.keys(porFecha).sort().reverse().map(fecha => (
        <div key={fecha} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#6B7280', textTransform: 'capitalize', marginBottom: 6 }}>
            {format(new Date(fecha+'T12:00'), "EEEE d 'de' MMM", { locale: es })}
          </div>
          {porFecha[fecha].map(o => (
            <div key={o.id} style={{ background: 'white', borderRadius: 12, padding: '12px 14px', marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: '#111827' }}>🧪 {o.mix_nombre}</div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{o.kg_producidos} kg producidos</div>
                  {o.merma_kg > 0 && <div style={{ fontSize: 11, color: '#EF4444', fontWeight: 600 }}>Merma: {o.merma_kg} kg</div>}
                  {o.merma_kg < 0 && <div style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>Excedente: {Math.abs(o.merma_kg)} kg</div>}
                  {o.costo_por_kg > 0 && esGerencia && (
                    <div style={{ fontSize: 11, color: '#2563EB', fontWeight: 600 }}>${COP(o.costo_por_kg)}/kg · Total: ${COP(o.costo_total)}</div>
                  )}
                  {o.notas && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{o.notas}</div>}
                </div>
                <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 10, fontWeight: 700, background: o.estado === 'enviado' ? '#D1FAE5' : '#FEF3C7', color: o.estado === 'enviado' ? '#065F46' : '#92400E', whiteSpace: 'nowrap' }}>
                  {o.estado === 'enviado' ? '✅' : '📝'}
                </span>
              </div>
              {o.bandejas?.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {o.bandejas.map((b, i) => (
                    <div key={i} style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '3px 8px', fontSize: 11 }}>
                      <strong>{b.sabor_nombre}</strong> · {b.tipo_bandeja}kg · <span style={{ fontWeight: 700, color: '#0369A1' }}>{b.cantidad}u</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}

      <button onClick={abrirModal} style={{ position: 'fixed', right: 20, bottom: 'calc(86px + env(safe-area-inset-bottom, 0px))', width: 52, height: 52, borderRadius: '50%', border: 'none', background: '#1a1a2e', color: 'white', fontSize: 26, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.25)', zIndex: 80 }}>+</button>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#1a1a2e', color: 'white', padding: '16px 20px', flexShrink: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>📋 Nueva orden de producción</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><span style={labelSt}>Fecha</span><input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} max={hoy} style={inputSt} /></div>
                <div>
                  <span style={labelSt}>Mix producido</span>
                  <select value={form.mix_id} onChange={e => setForm(f => ({ ...f, mix_id: e.target.value }))} style={inputSt}>
                    <option value="">Seleccionar...</option>
                    {mixes.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><span style={labelSt}>Kg producidos</span><input type="number" value={form.kg_producidos} onChange={e => setForm(f => ({ ...f, kg_producidos: e.target.value }))} placeholder="0.0" style={{ ...inputSt, fontFamily: 'monospace' }} /></div>
                <div><span style={labelSt}>Notas</span><input value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} placeholder="Opcional" style={inputSt} /></div>
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={labelSt}>Bandejas resultantes</span>
                  <button onClick={addBandeja} style={{ padding: '5px 12px', border: 'none', borderRadius: 6, background: '#1a1a2e', color: 'white', cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>+ Agregar</button>
                </div>
                {bandejas.length === 0 ? (
                  <div style={{ padding: 14, background: '#F9FAFB', borderRadius: 8, textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>Agrega las bandejas producidas</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {bandejas.map((b, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, background: '#F9FAFB', borderRadius: 8, padding: 8 }}>
                        <select value={b.sabor_id} onChange={e => updateBandeja(i, 'sabor_id', e.target.value)} style={{ ...inputSt, fontSize: 12 }}>
                          <option value="">Sabor...</option>
                          {sabores.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                        </select>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <select value={b.tipo_bandeja} onChange={e => updateBandeja(i, 'tipo_bandeja', parseFloat(e.target.value))} style={{ ...inputSt, fontSize: 12, flex: 1 }}>
                            {TIPOS_BANDEJA.map(t => <option key={t} value={t}>{t} kg</option>)}
                          </select>
                          <input type="number" value={b.cantidad} onChange={e => updateBandeja(i, 'cantidad', e.target.value)} placeholder="Cant." style={{ ...inputSt, fontSize: 12, flex: 1, fontFamily: 'monospace' }} />
                          <button onClick={() => removeBandeja(i)} style={{ padding: '0 12px', border: '1px solid #FCA5A5', borderRadius: 6, background: '#FEF2F2', cursor: 'pointer', color: '#DC2626', fontWeight: 700 }}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {bandejas.length > 0 && num(form.kg_producidos) > 0 && (
                  <div style={{ marginTop: 8, padding: '9px 12px', background: merma > 0.1 ? '#FEF3C7' : merma < -0.1 ? '#FEF2F2' : '#D1FAE5', borderRadius: 8, fontSize: 12 }}>
                    <div style={{ fontWeight: 700 }}>Kg en bandejas: {kgEnBandejas.toFixed(2)} kg</div>
                    <div style={{ color: Math.abs(merma) > 0.1 ? '#92400E' : '#065F46', fontWeight: 700 }}>
                      {merma > 0.1 ? `Merma: ${merma.toFixed(2)} kg` : merma < -0.1 ? `Excedente: ${Math.abs(merma).toFixed(2)} kg` : '✅ Cuadra perfecto'}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '14px 20px', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
              <button onClick={() => setModal(false)} style={{ flex: 1, padding: '11px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Cancelar</button>
              <button onClick={() => handleGuardar(false)} disabled={guardando} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#F59E0B', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>💾 Borrador</button>
              <button onClick={() => handleGuardar(true)} disabled={guardando} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#059669', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{guardando ? '⏳' : '✅ Enviar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// TAB SEMIELABORADOS
// ─────────────────────────────────────────
function TabSemielaborados({ esAdmin }) {
  const inicioMes = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const [semis, setSemis] = useState([]);
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [rango, setRango] = useState('mes');
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ item_id: '', fecha: hoy, kg_entrada: '', kg_producidos: '', notas: '' });

  const desde = rango === 'hoy' ? hoy : inicioMes;
  const hasta = hoy;

  useEffect(() => { API.get('/semi/items').then(r => setSemis(r.data)).catch(() => {}); }, []);
  useEffect(() => { cargar(); }, [rango]);

  const cargar = () => {
    setCargando(true);
    API.get('/semi/registros', { params: { desde, hasta } }).then(r => setRegistros(r.data)).catch(() => toast.error('Error cargando registros')).finally(() => setCargando(false));
  };

  const semiSel = semis.find(s => String(s.id) === String(form.item_id));
  const esFruta = semiSel?.categoria === 'Frutas procesadas';
  const mermaCalc = esFruta && form.kg_entrada && form.kg_producidos ? (parseFloat(form.kg_entrada) - parseFloat(form.kg_producidos)).toFixed(2) : null;
  const mermaPorc = mermaCalc && form.kg_entrada ? ((parseFloat(mermaCalc) / parseFloat(form.kg_entrada)) * 100).toFixed(1) : null;

  const resetForm = () => { setModal(false); setEditId(null); setForm({ item_id: '', fecha: hoy, kg_entrada: '', kg_producidos: '', notas: '' }); };
  const abrirEditar = (r) => { setEditId(r.id); setForm({ item_id: String(r.item_id), fecha: r.fecha?.slice(0,10), kg_entrada: r.kg_entrada||'', kg_producidos: r.kg_producidos, notas: r.notas||'' }); setModal(true); };

  const eliminar = async (r) => {
    if (!window.confirm(r.estado === 'enviado' ? '¿Eliminar este registro enviado? Se revertirá el stock.' : '¿Eliminar este borrador?')) return;
    try { await API.delete(`/semi/registros/${r.id}`); toast.success('Registro eliminado'); cargar(); }
    catch (e) { toast.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const guardar = async (enviar) => {
    if (!form.item_id || !form.kg_producidos) { toast.error('Completa semielaborado y kg producidos'); return; }
    if (esFruta && !form.kg_entrada) { toast.error('Para frutas ingresa los kg de fruta entrada'); return; }
    setGuardando(true);
    try {
      if (editId) await API.put(`/semi/registros/${editId}`, { ...form, enviar });
      else await API.post('/semi/registros', { ...form, enviar });
      toast.success(enviar ? '✅ Registro enviado — stock actualizado' : 'Borrador guardado');
      resetForm();
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Pill activo={rango === 'hoy'} onClick={() => setRango('hoy')}>Hoy</Pill>
        <Pill activo={rango === 'mes'} onClick={() => setRango('mes')}>Este mes</Pill>
      </div>

      {/* Stock rápido */}
      {semis.filter(s => s.stock > 0).length > 0 && (
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
          {semis.filter(s => s.stock > 0).map(s => (
            <div key={s.id} style={{ background: 'white', borderRadius: 10, padding: '9px 12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', minWidth: 130, flexShrink: 0 }}>
              <div style={{ fontSize: 10, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase' }}>{s.nombre}</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: 14, color: '#059669' }}>{COP(s.stock)} kg</div>
            </div>
          ))}
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Cargando...</div>
      ) : registros.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🧴</div>
          <div style={{ fontWeight: 600 }}>Sin registros en este período</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {registros.map(r => {
            const mPct = r.merma && r.kg_entrada ? ((parseFloat(r.merma)/parseFloat(r.kg_entrada))*100).toFixed(1) : null;
            return (
              <div key={r.id} style={{ background: 'white', borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{r.item_nombre}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{r.categoria} · {r.fecha?.slice(0,10)}</div>
                  </div>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: r.estado === 'enviado' ? '#D1FAE515' : '#FEF3C7', color: r.estado === 'enviado' ? '#059669' : '#F59E0B' }}>{r.estado}</span>
                </div>
                <div style={{ display: 'flex', gap: 14, marginTop: 8, fontSize: 12 }}>
                  {r.kg_entrada && <span style={{ color: '#6B7280' }}>Entrada: <strong style={{ fontFamily: 'monospace' }}>{COP(r.kg_entrada)} kg</strong></span>}
                  <span style={{ color: '#6B7280' }}>Producido: <strong style={{ fontFamily: 'monospace', color: '#111827' }}>{COP(r.kg_producidos)} kg</strong></span>
                  {r.merma != null && <span style={{ color: r.merma > 0 ? '#EF4444' : '#9CA3AF' }}>Merma: <strong style={{ fontFamily: 'monospace' }}>{COP(r.merma)} kg{mPct ? ` (${mPct}%)` : ''}</strong></span>}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={() => abrirEditar(r)} style={{ padding: '4px 10px', border: '1px solid #E5E7EB', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11 }}>✏️ Editar</button>
                  {esAdmin && <button onClick={() => eliminar(r)} style={{ padding: '4px 10px', border: '1px solid #FECACA', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11, color: '#EF4444' }}>🗑️ Eliminar</button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button onClick={() => setModal(true)} style={{ position: 'fixed', right: 20, bottom: 'calc(86px + env(safe-area-inset-bottom, 0px))', width: 52, height: 52, borderRadius: '50%', border: 'none', background: '#1a1a2e', color: 'white', fontSize: 26, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.25)', zIndex: 80 }}>+</button>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#1a1a2e', color: 'white', padding: '16px 20px', flexShrink: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{editId ? '✏️ Editar registro' : '🧴 Nuevo registro'}</div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <span style={labelSt}>Semielaborado *</span>
                <select value={form.item_id} onChange={e => setForm(f => ({ ...f, item_id: e.target.value }))} style={inputSt}>
                  <option value="">Seleccionar...</option>
                  {['Semielaborados','Frutas procesadas'].map(cat => (
                    <optgroup key={cat} label={cat}>
                      {semis.filter(s => s.categoria === cat).map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div><span style={labelSt}>Fecha *</span><input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} style={inputSt} /></div>
              {esFruta && (
                <div><span style={labelSt}>Kg de fruta entrada (antes de despulpar) *</span><input type="number" value={form.kg_entrada} onChange={e => setForm(f => ({ ...f, kg_entrada: e.target.value }))} placeholder="0" style={inputSt} /></div>
              )}
              <div><span style={labelSt}>{esFruta ? 'Kg de pulpa obtenida *' : 'Kg producidos *'}</span><input type="number" value={form.kg_producidos} onChange={e => setForm(f => ({ ...f, kg_producidos: e.target.value }))} placeholder="0" style={inputSt} /></div>
              {mermaCalc !== null && (
                <div style={{ background: parseFloat(mermaCalc) > 0 ? '#FEF2F2' : '#F0FDF4', borderRadius: 10, padding: '9px 12px' }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: parseFloat(mermaCalc) > 0 ? '#EF4444' : '#059669' }}>Merma: {mermaCalc} kg ({mermaPorc}%)</div>
                  <div style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>Peso cáscaras, semillas y pérdida del proceso</div>
                </div>
              )}
              <div><span style={labelSt}>Notas</span><input value={form.notas} onChange={e => setForm(f => ({ ...f, notas: e.target.value }))} placeholder="Opcional" style={inputSt} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, padding: '14px 20px', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
              <button onClick={resetForm} style={{ flex: 1, padding: '11px 0', border: '2px solid #E5E7EB', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Cancelar</button>
              <button onClick={() => guardar(false)} disabled={guardando} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#F59E0B', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>💾 Borrador</button>
              <button onClick={() => guardar(true)} disabled={guardando} style={{ flex: 1, padding: '11px 0', border: 'none', borderRadius: 10, background: '#059669', color: 'white', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{guardando ? '⏳' : '✅ Enviar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// PANTALLA — /produccion-helados
// ─────────────────────────────────────────
export default function ProduccionHelados() {
  const { usuario } = useAuth();
  const esGerencia = usuario?.rol === 'gerencia';
  const esAdmin = ['gerencia', 'administracion'].includes(usuario?.rol);
  const [tab, setTab] = useState('ordenes');

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#111827' }}>🍦 Producción Helados</h1>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>Órdenes y semielaborados</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Pill activo={tab === 'ordenes'} onClick={() => setTab('ordenes')}>📋 Órdenes</Pill>
        <Pill activo={tab === 'semis'} onClick={() => setTab('semis')}>🧴 Semielaborados</Pill>
      </div>

      {tab === 'ordenes' ? <TabOrdenes esGerencia={esGerencia} /> : <TabSemielaborados esAdmin={esAdmin} />}
    </div>
  );
}
